import assign from "@single-js/common/util/assign";
import { isLoadingError, LEError, LELoading, LESuccess, updateLoadingError, } from "@single-js/common/util/loading-error";
import { selectError, selectIsError, selectIsInitial, selectIsSuccess, selectLastUpdate, selectLoadingOrInitial, selectPayload, } from "@single-js/common/util/loading-state-machine";
import BigNumber from "@single-js/common/vendor/bignumber.js/bignumber";
import { formatNodeAddress, } from "data/models";
import { deriveDatabaseImpacted, deriveDatabaseStatus, derivePartitions, selectClusterStatisticsError, } from "data/selectors/cluster-metadata";
import { selectPipelines, selectPipelinesError, } from "data/selectors/pipelines";
import { selectRoute } from "data/selectors/routes";
import { deriveNodes, selectTopologyError, selectTopologyLastUpdate, selectTopologyLoading, } from "data/selectors/topology";
import _ from "lodash";
import { genTableId } from "memsql/db-object-id";
import { COLUMN_COLUMNS, DATABASE_COLUMNS, INDEX_COLUMNS, STORED_PROCEDURE_COLUMNS, TABLE_COLUMNS, UDA_COLUMNS, UDF_COLUMNS, VIEW_COLUMN_COLUMNS, VIEW_COLUMNS, } from "memsql/schema-column-info";
import { createSelector } from "reselect";
import { count } from "util/count";
import PIPELINES_COLUMNS from "view/pipelines/columns-info";
const selectSchemaSort = (kind) => (s) => s.schema.sort[kind];
export const selectColumns = (s) => s.schema.structure.columns;
export const selectViewColumns = (s) => s.schema.structure.viewColumns;
export const selectIndexes = (s) => s.schema.indexes;
export const selectDatabases = (s) => {
    return s.schema.structure.databases;
};
export function selectDatabaseNames(s) {
    const schemaSummary = selectPayload(s.schema.summary);
    if (schemaSummary) {
        return schemaSummary.databaseNames;
    }
}
export const selectTables = (s) => s.schema.structure.tables;
export const selectViews = (s) => s.schema.structure.views;
export const selectStoredProcedures = (s) => s.schema.structure.storedProcedures;
export const selectUDFs = (s) => s.schema.structure.userDefinedFunctions;
export const selectUDAs = (s) => s.schema.structure.aggregates;
export const selectCurrentDatabaseName = (s) => selectRoute(s).params.databaseName;
export const selectCurrentTableName = (s) => selectRoute(s).params.tableName;
export const selectCurrentDatabase = createSelector(selectCurrentDatabaseName, selectDatabases, (currentDatabaseName, databases) => {
    if (currentDatabaseName && databases.isSuccess()) {
        return _.find(databases.value, (db) => db.databaseName === currentDatabaseName);
    }
});
export const selectCurrentTableLike = createSelector(selectCurrentDatabaseName, selectCurrentTableName, selectTables, selectViews, (currentDatabaseName, currentTableName, tables, views) => {
    if (currentDatabaseName) {
        const tablesPerDatabase = tables[currentDatabaseName];
        const viewsPerDatabase = views[currentDatabaseName];
        if (currentTableName &&
            tablesPerDatabase &&
            tablesPerDatabase.isSuccess() &&
            viewsPerDatabase &&
            viewsPerDatabase.isSuccess()) {
            return _.find(_.concat(tablesPerDatabase.value, viewsPerDatabase.value), (t) => t.databaseName === currentDatabaseName &&
                t.tableName === currentTableName);
        }
    }
});
export const selectCurrentTable = (s) => selectRoute(s).params.tableName;
export const deriveColumns = createSelector(selectColumns, (columns) => {
    if (columns.isLoading()) {
        return new LELoading();
    }
    else if (columns.isError()) {
        return new LEError();
    }
    const maxDiskUsagePerTable = _.reduce(columns.value, (maxPerTable, column) => {
        const tableId = genTableId(column);
        const columnDiskUsage = column.statistics
            ? column.statistics.compressedSize
            : new LELoading();
        if (tableId in maxPerTable) {
            maxPerTable[tableId] = updateLoadingError(maxPerTable[tableId], (currentMax) => updateLoadingError(columnDiskUsage, (columnDiskUsage) => {
                if (columnDiskUsage.gt(currentMax)) {
                    return new LESuccess(columnDiskUsage);
                }
                else {
                    return new LESuccess(currentMax);
                }
            }));
        }
        else {
            maxPerTable[tableId] = columnDiskUsage;
        }
        return maxPerTable;
    }, {});
    const maxMemoryUsagePerTable = _.reduce(columns.value, (maxPerTable, column) => {
        const tableId = genTableId(column);
        const columnMemoryUsage = column.statistics
            ? column.statistics.memoryUse
            : new LELoading();
        if (tableId in maxPerTable &&
            maxPerTable[tableId] !== undefined) {
            maxPerTable[tableId] = updateLoadingError(maxPerTable[tableId], (currentMax) => updateLoadingError(columnMemoryUsage, (columnMemoryUsage) => {
                if (columnMemoryUsage.gt(currentMax)) {
                    return new LESuccess(columnMemoryUsage);
                }
                else {
                    return new LESuccess(currentMax);
                }
            }));
        }
        else {
            maxPerTable[tableId] = columnMemoryUsage;
        }
        return maxPerTable;
    }, {});
    return new LESuccess(_.map(columns.value, (column) => {
        let diskUsagePercent = new LELoading();
        let memoryUsagePercent = new LELoading();
        if (column.statistics) {
            diskUsagePercent = updateLoadingError(column.statistics.compressedSize, (compressedSize) => {
                return updateLoadingError(maxDiskUsagePerTable[genTableId(column)], (maxDiskUsage) => {
                    if (maxDiskUsage.isZero()) {
                        return new LESuccess(0);
                    }
                    else {
                        return new LESuccess(compressedSize
                            .dividedBy(maxDiskUsage)
                            .toNumber());
                    }
                });
            });
        }
        if (column.statistics) {
            memoryUsagePercent = updateLoadingError(column.statistics.memoryUse, (memoryUse) => {
                return updateLoadingError(maxMemoryUsagePerTable[genTableId(column)], (maxMemoryUsage) => {
                    if (maxMemoryUsage.isZero()) {
                        return new LESuccess(0);
                    }
                    else {
                        return new LESuccess(memoryUse
                            .dividedBy(maxMemoryUsage)
                            .toNumber());
                    }
                });
            });
        }
        return {
            column,
            derived: {
                diskUsagePercent,
                memoryUsagePercent,
            },
        };
    }));
});
export const deriveTables = createSelector(selectTables, selectColumns, (tables, columns) => {
    let derivedTables = {};
    Object.keys(tables).forEach((databaseName) => {
        let derivedTablesByDatabase = new LELoading();
        const allTables = {};
        const tablesPerDb = tables[databaseName];
        if (tablesPerDb.isSuccess()) {
            for (let i = 0; i < tablesPerDb.value.length; i++) {
                const table = tablesPerDb.value[i];
                allTables[table.tableId] = {
                    table,
                    derived: {
                        diskUsage: new LESuccess(new BigNumber(0)),
                        uncompressedSize: new LESuccess(new BigNumber(0)),
                        diskUsagePercent: new LELoading(),
                        memoryUsagePercent: new LELoading(),
                    },
                };
            }
            derivedTablesByDatabase = new LESuccess(allTables);
        }
        else if (tablesPerDb.isLoading()) {
            derivedTables = {
                ...derivedTables,
                [databaseName]: new LELoading(),
            };
            // we don't want to continue calculating schema for the loading tables
            return;
        }
        else {
            derivedTables = {
                ...derivedTables,
                [databaseName]: new LEError(tablesPerDb.errorValue),
            };
            // we don't want to continue calculating schema for the loading tables
            return;
        }
        if (columns.isSuccess()) {
            const columnsByDatabase = columns.value.filter((column) => column.databaseName === databaseName);
            for (let i = 0; i < columnsByDatabase.length; i++) {
                const column = columnsByDatabase[i];
                const tableId = genTableId(column);
                const derivedTable = derivedTablesByDatabase.value[tableId];
                if (column.statistics && derivedTable) {
                    const { derived } = derivedTable;
                    const diskUsage = updateLoadingError(column.statistics.compressedSize, (columnDiskUsage) => {
                        return updateLoadingError(derived.diskUsage, (currentDiskUsage) => {
                            return new LESuccess(currentDiskUsage.plus(columnDiskUsage));
                        });
                    });
                    const uncompressedSize = updateLoadingError(column.statistics.uncompressedSize, (columnUncompressedSize) => {
                        return updateLoadingError(derived.uncompressedSize, (currentUncompressedSize) => {
                            return new LESuccess(currentUncompressedSize.plus(columnUncompressedSize));
                        });
                    });
                    if (derivedTablesByDatabase.value[tableId]) {
                        derivedTablesByDatabase.value[tableId].derived = {
                            ...derived,
                            diskUsage,
                            uncompressedSize,
                        };
                    }
                }
            }
        }
        const maxDiskUsagePerDatabase = _.reduce(derivedTablesByDatabase.value, (maxPerDatabase, { table, derived }) => {
            const databaseName = table.databaseName;
            const tableDiskUsage = derived.diskUsage;
            const currentMax = maxPerDatabase[databaseName];
            if (databaseName in maxPerDatabase) {
                maxPerDatabase[databaseName] = updateLoadingError(maxPerDatabase[databaseName], (currentMax) => updateLoadingError(tableDiskUsage, (tableDiskUsage) => {
                    if (tableDiskUsage.gt(currentMax)) {
                        return new LESuccess(tableDiskUsage);
                    }
                    else {
                        return new LESuccess(currentMax);
                    }
                }));
            }
            else {
                maxPerDatabase[databaseName] = tableDiskUsage;
            }
            if (currentMax &&
                currentMax.isSuccess() &&
                tableDiskUsage &&
                tableDiskUsage.isSuccess()) {
                if (tableDiskUsage.value.gt(currentMax.value)) {
                    maxPerDatabase[databaseName] = tableDiskUsage;
                }
            }
            else if (currentMax === undefined) {
                maxPerDatabase[databaseName] = tableDiskUsage;
            }
            return maxPerDatabase;
        }, {});
        const maxMemoryUsagePerDatabase = _.reduce(derivedTablesByDatabase.value, (maxPerDatabase, { table }) => {
            const databaseName = table.databaseName;
            const tableMemoryUsage = table.statistics
                ? table.statistics.memoryUse
                : new BigNumber(0);
            if (maxPerDatabase[databaseName]) {
                maxPerDatabase[databaseName] = BigNumber.max(maxPerDatabase[databaseName], tableMemoryUsage);
            }
            else {
                maxPerDatabase[databaseName] = tableMemoryUsage;
            }
            return maxPerDatabase;
        }, {});
        derivedTables = {
            ...derivedTables,
            [databaseName]: new LESuccess(_.map(derivedTablesByDatabase.value, ({ table, derived }) => {
                const maxMemoryUsage = maxMemoryUsagePerDatabase[table.databaseName] ||
                    new BigNumber(0);
                const diskUsagePercent = updateLoadingError(derived.diskUsage, (tableDiskUsage) => {
                    // We special case tables with 0 disk usage and
                    // immediately know that their diskUsagePercent is 0.
                    if (tableDiskUsage.isZero()) {
                        return new LESuccess(0);
                    }
                    return updateLoadingError(maxDiskUsagePerDatabase[table.databaseName], (maxDiskUsage) => {
                        if (maxDiskUsage.isZero()) {
                            return new LESuccess(0);
                        }
                        else {
                            return new LESuccess(tableDiskUsage
                                .dividedBy(maxDiskUsage)
                                .toNumber());
                        }
                    });
                });
                let memoryUsagePercent = new LELoading();
                if (table.statistics) {
                    if (maxMemoryUsage.isZero()) {
                        memoryUsagePercent = new LESuccess(0);
                    }
                    else {
                        memoryUsagePercent = new LESuccess(table.statistics.memoryUse
                            .dividedBy(maxMemoryUsage)
                            .toNumber());
                    }
                }
                return {
                    table,
                    derived: {
                        ...derived,
                        diskUsagePercent,
                        memoryUsagePercent,
                    },
                };
            })),
        };
    });
    return derivedTables;
});
export const selectSortedTables = createSelector(selectCurrentDatabaseName, selectSchemaSort("TABLE"), deriveTables, (databaseName, sort, tables) => {
    if (databaseName) {
        const filteredTables = tables[databaseName];
        if (filteredTables && filteredTables.isLoading()) {
            return new LELoading();
        }
        else if (filteredTables && filteredTables.isError()) {
            return new LEError();
        }
        if (sort) {
            const { direction, columnId } = sort;
            const columnInfo = _.find(TABLE_COLUMNS, (col) => col.id === columnId);
            if (!columnInfo) {
                throw new Error("Expected column to be defined.");
            }
            return new LESuccess(_.orderBy(filteredTables.value, [columnInfo.getValue], direction));
        }
        return filteredTables;
    }
    return new LEError();
});
const deriveViews = createSelector(selectViews, selectViewColumns, (views, viewColumns) => {
    let derivedViews = {};
    Object.keys(views).forEach((databaseName) => {
        const derivedViewsPerDb = {};
        const viewsPerDb = views[databaseName];
        if (viewsPerDb.isSuccess()) {
            for (let i = 0; i < viewsPerDb.value.length; i++) {
                const view = viewsPerDb.value[i];
                derivedViewsPerDb[view.tableId] = {
                    view,
                    derived: {
                        columnCount: 0,
                    },
                };
            }
        }
        else if (viewsPerDb.isLoading()) {
            derivedViews = {
                ...derivedViews,
                [databaseName]: new LELoading(),
            };
            // we don't want to continue calculating schema for the loading views
            return;
        }
        else {
            derivedViews = {
                ...derivedViews,
                [databaseName]: new LEError(viewsPerDb.errorValue),
            };
            // we don't want to continue calculating schema for the loading views
            return;
        }
        if (viewColumns.isSuccess()) {
            const viewsColumnsByDatabase = viewColumns.value.filter((viewColumn) => viewColumn.databaseName === databaseName);
            for (let i = 0; i < viewsColumnsByDatabase.length; i++) {
                const viewColumn = viewsColumnsByDatabase[i];
                const viewId = genTableId(viewColumn);
                const { derived } = derivedViewsPerDb[viewId];
                derivedViewsPerDb[viewId].derived = {
                    columnCount: derived.columnCount + 1,
                };
            }
        }
        derivedViews[databaseName] = new LESuccess(_.map(derivedViewsPerDb));
    });
    return derivedViews;
});
export const selectSortedViews = createSelector(selectCurrentDatabaseName, deriveViews, selectSchemaSort("VIEW"), (currentDatabase, views, sort) => {
    if (currentDatabase) {
        const selectedViews = views[currentDatabase];
        if (selectedViews && selectedViews.isSuccess()) {
            if (sort) {
                const { direction, columnId } = sort;
                const columnInfo = _.find(VIEW_COLUMNS, (col) => col.id === columnId);
                if (!columnInfo) {
                    throw new Error("Expected column to be defined.");
                }
                return new LESuccess(_.orderBy(selectedViews.value, [columnInfo.getValue], direction));
            }
            else {
                return selectedViews;
            }
        }
        else if (selectedViews && selectedViews.isLoading()) {
            return new LELoading();
        }
    }
    return new LEError();
});
export const selectSortedStoredProcedures = createSelector(selectCurrentDatabaseName, selectStoredProcedures, selectSchemaSort("STORED_PROCEDURE"), (currentDatabase, storedProcedures, sort) => {
    if (storedProcedures.isLoading()) {
        return new LELoading();
    }
    else if (storedProcedures.isError()) {
        return new LEError();
    }
    const selectedSortedProcedures = _.filter(storedProcedures.value, (sp) => sp.databaseName === currentDatabase);
    if (sort) {
        const { direction, columnId } = sort;
        const columnInfo = _.find(STORED_PROCEDURE_COLUMNS, (col) => col.id === columnId);
        if (!columnInfo) {
            throw new Error("Expected column to be defined.");
        }
        return new LESuccess(_.orderBy(selectedSortedProcedures, [columnInfo.getValue], direction));
    }
    else {
        return new LESuccess(selectedSortedProcedures);
    }
});
export const selectSortedUDFs = createSelector(selectCurrentDatabaseName, selectUDFs, selectSchemaSort("USER_DEFINED_FUNCTION"), (currentDatabase, userDefinedFunctions, sort) => {
    if (userDefinedFunctions.isLoading()) {
        return new LELoading();
    }
    else if (userDefinedFunctions.isError()) {
        return new LEError();
    }
    const selectedUDFs = _.filter(userDefinedFunctions.value, (udf) => udf.databaseName === currentDatabase);
    if (sort) {
        const { direction, columnId } = sort;
        const columnInfo = _.find(UDF_COLUMNS, (col) => col.id === columnId);
        if (!columnInfo) {
            throw new Error("Expected column to be defined.");
        }
        return new LESuccess(_.orderBy(selectedUDFs, [columnInfo.getValue], direction));
    }
    else {
        return new LESuccess(selectedUDFs);
    }
});
export const selectSortedUDAs = createSelector(selectCurrentDatabaseName, selectUDAs, selectSchemaSort("USER_DEFINED_AGGREGATE"), (currentDatabase, aggregates, sort) => {
    if (aggregates.isLoading()) {
        return new LELoading();
    }
    else if (aggregates.isError()) {
        return new LEError();
    }
    const selectedUDAs = _.filter(aggregates.value, (aggregate) => aggregate.databaseName === currentDatabase);
    if (sort) {
        const { direction, columnId } = sort;
        const columnInfo = _.find(UDA_COLUMNS, (col) => col.id === columnId);
        if (!columnInfo) {
            throw new Error("Expected column to be defined.");
        }
        return new LESuccess(_.orderBy(selectedUDAs, [columnInfo.getValue], direction));
    }
    else {
        return new LESuccess(selectedUDAs);
    }
});
export const deriveDatabases = createSelector(selectDatabases, deriveTables, derivePartitions, deriveNodes, (databases, tables, derivedPartitions, derivedNodes) => {
    if (derivedPartitions) {
        const derivedDatabases = {};
        if (databases.isSuccess()) {
            for (let i = 0; i < databases.value.length; i++) {
                const database = databases.value[i];
                const databasePartitions = _.filter(derivedPartitions, {
                    databaseName: database.databaseName,
                });
                let dataPartitionInstanceNodeCount = 0;
                let totalLeafCount = 0;
                if (derivedNodes) {
                    const dataPartitions = _.filter(databasePartitions, (part) => part.kind === "data");
                    const dataPartitionInstances = _.flatMap(dataPartitions, (dataPart) => dataPart.partitionInstances);
                    dataPartitionInstanceNodeCount = _.uniqBy(dataPartitionInstances, formatNodeAddress).length;
                    totalLeafCount = _.filter(derivedNodes, (node) => node.role === "LEAF").length;
                }
                // We want the values to be loading by default, unless we don't have
                // any tables in the database. In that case, we can set those values
                // to 0 since they are not going to be overwritten on the tables loop.
                let defaultValue = new LELoading();
                let percentDefaultValue = new LELoading();
                if (database.tableCount === 0) {
                    defaultValue = new LESuccess(new BigNumber(0));
                    percentDefaultValue = new LESuccess(0);
                }
                derivedDatabases[database.databaseName] = {
                    database,
                    derived: {
                        dataPartitionInstanceNodeCount,
                        totalLeafCount,
                        memoryUsage: new BigNumber(0),
                        memoryUsagePercent: percentDefaultValue,
                        diskUsage: defaultValue,
                        diskUsagePercent: percentDefaultValue,
                        uncompressedSize: new LESuccess(new BigNumber(0)),
                        status: deriveDatabaseStatus(database.databaseName, databasePartitions),
                        impacted: deriveDatabaseImpacted(database.databaseName, databasePartitions),
                    },
                };
                const tablesPerDatabase = tables[database.databaseName];
                if (tablesPerDatabase && tablesPerDatabase.isSuccess()) {
                    for (let i = 0; i < tablesPerDatabase.value.length; i++) {
                        const table = tablesPerDatabase.value[i];
                        const databaseName = table.table.databaseName;
                        const { derived } = derivedDatabases[databaseName];
                        const tableStatistics = table.table.statistics;
                        let diskUsage = new LELoading();
                        if (derived.diskUsage.isLoading() &&
                            table.derived.diskUsage.isSuccess()) {
                            diskUsage = new LESuccess(table.derived.diskUsage.value);
                        }
                        else if (derived.diskUsage.isSuccess()) {
                            diskUsage = updateLoadingError(table.derived.diskUsage, (tableDiskUsage) => updateLoadingError(derived.diskUsage, (currentDiskUsage) => new LESuccess(currentDiskUsage.plus(tableDiskUsage))));
                        }
                        const uncompressedSize = updateLoadingError(table.derived.uncompressedSize, (tableUncompressedSize) => updateLoadingError(derived.uncompressedSize, (currentUncompressedSize) => new LESuccess(currentUncompressedSize.plus(tableUncompressedSize))));
                        const memoryUsage = derived.memoryUsage.plus(tableStatistics ? tableStatistics.memoryUse : 0);
                        derivedDatabases[databaseName].derived = assign(derived, {
                            diskUsage,
                            uncompressedSize,
                            memoryUsage,
                        });
                    }
                }
            }
        }
        let maxDiskUsage = new LESuccess(new BigNumber(0));
        _.forEach(derivedDatabases, ({ derived }) => {
            if (derived.diskUsage.isSuccess() && maxDiskUsage.isSuccess()) {
                if (derived.diskUsage.value.gt(maxDiskUsage.value)) {
                    maxDiskUsage = new LESuccess(derived.diskUsage.value);
                }
            }
        });
        let maxMemoryUsage = new BigNumber(0);
        _.forEach(derivedDatabases, ({ derived }) => {
            if (derived.memoryUsage.gt(maxMemoryUsage)) {
                maxMemoryUsage = derived.memoryUsage;
            }
        });
        return _.map(derivedDatabases, ({ database, derived }) => {
            const diskUsagePercent = updateLoadingError(derived.diskUsage, (databaseDiskUsage) => {
                // We special case databases with 0 disk usage and
                // immediately know that their diskUsagePercent is
                // 0.
                if (databaseDiskUsage.isZero()) {
                    return new LESuccess(0);
                }
                return updateLoadingError(maxDiskUsage, (maxDiskUsage) => {
                    if (maxDiskUsage.isZero()) {
                        return new LESuccess(0);
                    }
                    else {
                        return new LESuccess(databaseDiskUsage
                            .dividedBy(maxDiskUsage)
                            .toNumber());
                    }
                });
            });
            let memoryUsagePercent;
            if (maxMemoryUsage.isZero()) {
                memoryUsagePercent = new LESuccess(0);
            }
            else {
                memoryUsagePercent = new LESuccess(derived.memoryUsage
                    .dividedBy(maxMemoryUsage)
                    .toNumber());
            }
            return {
                database,
                derived: {
                    ...derived,
                    diskUsagePercent,
                    memoryUsagePercent,
                },
            };
        });
    }
});
export const selectSortedDatabases = createSelector(selectSchemaSort("DATABASE"), deriveDatabases, (sort, databases) => {
    if (sort) {
        const { direction, columnId } = sort;
        const databaseInfo = _.find(DATABASE_COLUMNS, (col) => col.id === columnId);
        if (!databaseInfo) {
            throw new Error("Expected column to be defined.");
        }
        return _.orderBy(databases, [databaseInfo.getValue], direction);
    }
    else {
        return databases;
    }
});
export const selectSortedColumns = createSelector(selectSchemaSort("TABLE_COLUMN"), deriveColumns, selectCurrentDatabaseName, selectCurrentTableName, (sort, columns, currentDatabase, currentTable) => {
    if (columns.isLoading()) {
        return new LELoading();
    }
    else if (columns.isError()) {
        return new LEError();
    }
    const selectedColumns = _.filter(columns.value, ({ column }) => column.databaseName === currentDatabase &&
        column.tableName === currentTable);
    if (sort) {
        const { direction, columnId } = sort;
        const columnInfo = _.find(COLUMN_COLUMNS, (col) => col.id === columnId);
        if (!columnInfo) {
            throw new Error("Expected column to be defined.");
        }
        return new LESuccess(_.orderBy(selectedColumns, [columnInfo.getValue], direction));
    }
    else {
        return new LESuccess(selectedColumns);
    }
});
export const selectSortedViewColumns = createSelector(selectSchemaSort("VIEW_COLUMN"), selectViewColumns, selectCurrentDatabaseName, selectCurrentTableName, (sort, columns, currentDatabase, currentTable) => {
    let selectedColumns = [];
    if (columns.isSuccess()) {
        selectedColumns = _.filter(columns.value, (column) => column.databaseName === currentDatabase &&
            column.tableName === currentTable);
    }
    if (sort) {
        const { direction, columnId } = sort;
        const columnInfo = _.find(VIEW_COLUMN_COLUMNS, (col) => col.id === columnId);
        if (!columnInfo) {
            throw new Error("Expected column to be defined.");
        }
        return _.orderBy(selectedColumns, [columnInfo.getValue], direction);
    }
    else {
        return selectedColumns;
    }
});
export const selectSortedIndexes = createSelector(selectSchemaSort("INDEX"), selectIndexes, selectCurrentDatabaseName, selectCurrentTableName, (sort, indexes, currentDatabase, currentTable) => {
    if (indexes.isLoading()) {
        return new LELoading();
    }
    else if (indexes.isError()) {
        return new LEError();
    }
    const selectedIndexes = _.filter(indexes.value, (index) => index.databaseName === currentDatabase &&
        index.tableName === currentTable);
    if (sort) {
        const { direction, columnId } = sort;
        const columnInfo = _.find(INDEX_COLUMNS, (col) => col.id === columnId);
        if (!columnInfo) {
            throw new Error("Expected column to be defined.");
        }
        return new LESuccess(_.orderBy(selectedIndexes, [columnInfo.getValue], direction));
    }
    else {
        return new LESuccess(selectedIndexes);
    }
});
export const selectCurrentDerivedTable = createSelector(selectCurrentDatabaseName, selectCurrentTableName, deriveTables, (databaseName, tableName, tables) => {
    if (databaseName) {
        const tablesPerDatabase = tables[databaseName];
        if (tablesPerDatabase && tablesPerDatabase.isSuccess()) {
            return _.find(tablesPerDatabase.value, ({ table }) => table.databaseName === databaseName &&
                table.tableName === tableName);
        }
    }
});
export const selectCurrentDerivedDatabase = createSelector(selectCurrentDatabaseName, deriveDatabases, (databaseName, databases) => {
    return _.find(databases, ({ database }) => database.databaseName === databaseName);
});
export const selectCurrentView = createSelector(selectCurrentDatabaseName, selectCurrentTableName, selectViews, (databaseName, tableName, views) => {
    if (databaseName) {
        const viewsPerDatabase = views[databaseName];
        if (viewsPerDatabase && viewsPerDatabase.isSuccess()) {
            return _.find(viewsPerDatabase.value, (view) => view.tableName === tableName);
        }
    }
});
function isAnyItemLoading(schema) {
    Object.values(schema).forEach((item) => {
        if (item && isLoadingError(item)) {
            if (item.isLoading()) {
                return true;
            }
        }
    });
    return false;
}
function anyItemNeedsLoading(schema) {
    Object.values(schema).forEach((item) => {
        if (item && isLoadingError(item)) {
            if (item.isError() || item.isLoading) {
                return true;
            }
        }
    });
    return false;
}
// We return `true` if ANY of the stores that the Schema Explorer
// depends on are in the loading state.
export const selectSchemaExplorerLoading = (s) => isAnyItemLoading(s.schema.structure) ||
    s.schema.statisticsLoading ||
    selectTopologyLoading(s) ||
    selectLoadingOrInitial(s.clusterMetadata.clusterStatistics) ||
    selectLoadingOrInitial(s.pipelines.pipelines) ||
    selectLoadingOrInitial(s.clusterMetadata.partitions);
// We return `true` if ANY of the stores that the Schema Explorer
// depends on are in the initial or error state, because that means we should
// issue a reload action.
export const selectSchemaExplorerNeedsLoading = (s) => !anyItemNeedsLoading(s.schema.structure) ||
    !(s.schema.statisticsLoading || s.schema.statisticsLoaded) ||
    selectIsInitial(s.topology.topology) ||
    selectIsError(s.topology.topology) ||
    selectIsInitial(s.clusterMetadata.clusterStatistics) ||
    !!selectError(s.clusterMetadata.clusterStatistics) ||
    selectIsInitial(s.pipelines.pipelines) ||
    selectIsInitial(s.clusterMetadata.partitions);
// We return `true` if and only if all of the stores that the Schema
// Explorer depends on have been loaded.
export const selectSchemaExplorerLoaded = (s) => s.schema.structure.databases.isSuccess() &&
    selectIsSuccess(s.topology.topology) &&
    selectIsSuccess(s.clusterMetadata.clusterStatistics) &&
    selectIsSuccess(s.pipelines.pipelines) &&
    selectIsSuccess(s.clusterMetadata.partitions);
// We return the first error we find that may have happened while getting the
// information that the SChema Explorer depends on.
export const selectSchemaExplorerError = (s) => s.schema.structureError ||
    s.schema.statisticsError ||
    selectPipelinesError(s) ||
    selectTopologyError(s) ||
    selectClusterStatisticsError(s) ||
    selectError(s.clusterMetadata.partitions);
// We look at all of the stores that the Schema Explorer depends on and
// return the date that is more recent.
export const selectSchemaExplorerLastUpdate = (s) => _.max([
    s.schema.lastStatisticsUpdate,
    selectTopologyLastUpdate(s),
    selectLastUpdate(s.clusterMetadata.clusterStatistics),
    selectLastUpdate(s.clusterMetadata.partitions),
]);
export const selectSchemaSummaryLoading = (s) => selectLoadingOrInitial(s.schema.summary);
export const selectSchemaSummaryLastUpdate = (s) => selectLastUpdate(s.schema.summary);
export const selectSchemaSummaryError = (s) => selectError(s.schema.summary);
// This method returns the entire state that the Schema Explorer depends
// on. If the schema explorer data has not yet been loaded, then it returns
// `undefined`.
export const selectSchemaFullPayload = (s) => {
    const clusterStatistics = selectPayload(s.clusterMetadata.clusterStatistics);
    const topology = selectPayload(s.topology.topology);
    const partitions = selectPayload(s.clusterMetadata.partitions);
    if (!clusterStatistics ||
        !topology ||
        !selectSchemaExplorerLoaded(s) ||
        !partitions) {
        return;
    }
    return {
        structure: s.schema.structure,
        clusterStatistics,
        indexes: s.schema.indexes,
        topology,
        partitions,
    };
};
export const selectSampleData = (s) => {
    return selectPayload(s.schema.sampleData);
};
export const selectSampleDataError = (s) => {
    return selectError(s.schema.sampleData);
};
export const selectSchemaPipelinesSort = selectSchemaSort("PIPELINE");
export const selectSchemaSortedPipelines = createSelector(selectCurrentDatabaseName, selectPipelines, selectSchemaPipelinesSort, (currentDatabase, pipelines, sort) => {
    if (pipelines) {
        const selectedPipelines = _.filter(pipelines, (pipeline) => pipeline.databaseName === currentDatabase);
        if (sort) {
            const { direction, columnId } = sort;
            const columnInfo = _.find(PIPELINES_COLUMNS, (col) => col.id === columnId);
            if (!columnInfo) {
                throw new Error("Expected column to be defined.");
            }
            return _.orderBy(selectedPipelines, [columnInfo.getValue], direction);
        }
        else {
            return selectedPipelines;
        }
    }
    return [];
});
export const deriveDatabasesMetadata = createSelector(selectDatabaseNames, derivePartitions, (databaseNames, derivedPartitions) => {
    if (databaseNames && derivedPartitions) {
        return _.map(databaseNames, (databaseName) => {
            const databasePartitions = _.filter(derivedPartitions, {
                databaseName,
            });
            return {
                databaseName,
                status: deriveDatabaseStatus(databaseName, databasePartitions),
                impacted: deriveDatabaseImpacted(databaseName, databasePartitions),
            };
        });
    }
});
export const selectAnyDatabasesOfflineWithoutPartitions = createSelector(deriveDatabases, derivePartitions, (derivedDatabases, derivedParititons) => {
    const dataPartitions = _.filter(derivedParititons, (partition) => partition.kind === "data" &&
        partition.partitionInstances.length > 0);
    const withPartitions = _.uniqBy(dataPartitions, "databaseName").map((partition) => partition.databaseName);
    if (derivedDatabases) {
        const numWithoutPartitionsAndOffline = count(derivedDatabases, (db) => db.derived.status === "offline" &&
            !withPartitions.includes(db.database.databaseName));
        return numWithoutPartitionsAndOffline > 0;
    }
    return false;
});
